import DOMPurify from 'dompurify';
import {
  Flex,
  View,
  Text,
  Heading,
  Button,
  Card,
  Badge,
  Loader,
  Alert,
  Divider
} from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';

const JobDetailsModal = ({
  showModal,
  job,
  onClose,
  onApply,
  onSave,
  onCheckCompatibility,
  isSaved = false,
  isSavingJob = false,
  compatibilityResult = null,
  showCompatibility = false,
  isCheckingCompatibility = false,
  compatibilityError = null,
  loadingFullDescription = false,
  fullDescriptionError = null,
  readOnly = false  // NEW: Control whether modal shows interactive features
}) => {
  if (!showModal || !job) return null;

  // Helper functions
  const getPostedText = (days) => {
    if (!days && days !== 0) return 'Posted recently';
    if (days === 0) return 'Posted today';
    if (days === 1) return 'Posted yesterday';
    return `Posted ${days} days ago`;
  };

  const formatJobDescription = (job) => {
    const description = job.fullDescription || job.description || 'No description available';
    
    if (job.source === 'adzuna-api'  || (job.id && job.id.toLowerCase().startsWith('adzuna'))) {
      let formatted = description;
      formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      formatted = formatted
        .replace(/(^|\. )(Benefits|Requirements|Responsibilities|Qualifications|Skills|Experience|Role|About|Overview|Summary|Duties|Key.+?:|What.+?:)/gm, '$1<br><br><strong>$2</strong>')
        .replace(/(Safeguarding|Equal Opportunities)/g, '<br><br><strong>$1</strong>');
      formatted = formatted.replace(/\. ([A-Z])/g, '.<br><br>$1');
      formatted = formatted.replace(/[•·▪▫◦‣⁃]\s*/g, '<br>• ');
      formatted = formatted.replace(/^\* /gm, '<br>• ');
      formatted = formatted.replace(/(<br>){3,}/g, '<br><br>');
      return `<p>${formatted}</p>`;
    }
    
    return description;
  };

  const getCompatibilityColor = (score) => {
    if (score >= 80) return '#2ECC71';
    if (score >= 60) return '#F39C12';
    if (score >= 40) return '#E67E22';
    return '#E74C3C';
  };

  const getCompatibilityLabel = (score) => {
    if (score >= 80) return 'Excellent Match';
    if (score >= 60) return 'Good Match';
    if (score >= 40) return 'Fair Match';
    return 'Poor Match';
  };

  // For read-only mode, check if job already has compatibility data
  const hasExistingCompatibility = readOnly && (job.score > 0 || compatibilityResult);
  const shouldShowCompatibility = readOnly ? hasExistingCompatibility : showCompatibility;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '2rem',
        overflowY: 'auto',
        backdropFilter: 'blur(5px)'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          background: 'rgba(30, 35, 45, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          maxWidth: shouldShowCompatibility ? '1400px' : '800px',
          width: '100%',
          maxHeight: '95vh',
          overflowY: 'auto',
          position: 'relative',
          transition: 'max-width 0.3s ease-in-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: 'white',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: '1.5rem',
            transition: 'all 0.2s',
            zIndex: 10,
            lineHeight: '1'
          }}
          onClick={onClose}
        >
          <span style={{ transform: 'translateY(-2px)' }}>×</span>
        </button>
        
        <View padding="2rem">
          {/* Header Section */}
          <Flex direction="column" gap="1rem" marginBottom="2rem">
            {/* Title with optional save button */}
            <View textAlign="center" style={{ position: 'relative', paddingLeft: readOnly ? '0' : '50px', paddingRight: readOnly ? '0' : '50px' }}>
              {!readOnly && onSave && (
                <button
                  onClick={() => onSave(job)}
                  aria-label={isSaved ? 'Unsave listing' : 'Save listing'}
                  title={isSaved ? 'Unsave listing' : 'Save listing'}
                  disabled={isSavingJob}
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    border: `2px solid ${isSaved ? '#2ECC71' : 'rgba(255,255,255,0.25)'}`,
                    background: isSaved ? 'rgba(46, 204, 113, 0.15)' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isSavingJob ? 'not-allowed' : 'pointer',
                    opacity: isSavingJob ? 0.5 : 1,
                    zIndex: 10
                  }}
                >
                  <svg 
                    width="20" 
                    height="24" 
                    viewBox="0 0 18 22" 
                    fill={isSaved ? '#2ECC71' : 'none'}
                    stroke={isSaved ? '#2ECC71' : 'rgba(255,255,255,0.8)'}
                    strokeWidth="2"
                  >
                    <path d="M1 3v18l8-5 8 5V3c0-1.1-.9-2-2-2H3c-1.1 0-2 .9-2 2z"/>
                  </svg>
                </button>
              )}

              <Heading 
                level={2} 
                style={{ 
                  color: 'white', 
                  fontSize: job.title?.length > 40 ? '1.5rem' : '2rem',
                  marginBottom: '0.5rem',
                  wordBreak: 'break-word',
                  lineHeight: '1.2'
                }}
              >
                {job.title}
              </Heading>
              <Text fontSize="1.2rem" color="rgba(255,255,255,0.9)" fontWeight="600">
                {job.company}
              </Text>
            </View>

            {/* Match Score and Buttons */}
            <Flex justifyContent="center" alignItems="center" gap="1rem">
              {compatibilityResult && compatibilityResult.score !== undefined && (
                <Badge 
                  style={{
                    backgroundColor: `${getCompatibilityColor(compatibilityResult.score)}20`,
                    color: getCompatibilityColor(compatibilityResult.score),
                    border: `1px solid ${getCompatibilityColor(compatibilityResult.score)}`,
                    fontSize: '0.9rem',
                    padding: '0.5rem 0.75rem',
                    fontWeight: '600'
                  }}
                >
                  {compatibilityResult.score}% Match
                </Badge>
              )}

              <Button
                size="medium"
                onClick={() => onApply(job)}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  color: 'white',
                  borderRadius: '8px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                Apply Now
              </Button>
              
              {/* Only show Check CV Compatibility button if not read-only and not already showing compatibility */}
              {!readOnly && !showCompatibility && onCheckCompatibility && (
                <Button
                  size="medium"
                  onClick={() => onCheckCompatibility(job)}
                  isDisabled={isCheckingCompatibility}
                  style={{
                    background: 'linear-gradient(135deg, #2ECC71 0%, #27AE60 100%)',
                    border: 'none',
                    color: 'white',
                    borderRadius: '8px',
                    padding: '0.5rem 1rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    boxShadow: '0 4px 15px rgba(46, 204, 113, 0.3)',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Check CV Compatibility
                </Button>
              )}
            </Flex>
          </Flex>

          <Flex gap="2rem" alignItems="flex-start">
            <View flex="1" style={{ minWidth: '350px' }}>
              <Card
                padding="1.5rem"
                marginBottom="2rem"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(102, 126, 234, 0.3)'
                }}
              >
                <Flex direction="column" gap="1rem">
                  <Flex gap="2rem" flexWrap="wrap">
                    <View>
                      <Text color="rgba(255,255,255,0.6)" fontSize="0.85rem" marginBottom="0.25rem">Location</Text>
                      <Text color="white" fontWeight="500">{job.location}</Text>
                      <Text color="rgba(255,255,255,0.7)" fontSize="0.9rem">({job.locationType || 'On-site'})</Text>
                    </View>
                    <View>
                      <Text color="rgba(255,255,255,0.6)" fontSize="0.85rem" marginBottom="0.25rem">Salary</Text>
                      <Text color="white" fontWeight="500">{job.salary || 'Not specified'}</Text>
                    </View>
                    <View>
                      <Text color="rgba(255,255,255,0.6)" fontSize="0.85rem" marginBottom="0.25rem">Posted</Text>
                      <Text color="white" fontWeight="500">{getPostedText(job.postedDays)}</Text>
                    </View>
                  </Flex>
                  
                  <Divider style={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                  
                  <Flex gap="0.5rem" flexWrap="wrap">
                    <Badge 
                      style={{
                        backgroundColor: 'rgba(102, 126, 234, 0.2)',
                        color: 'rgba(255,255,255,0.8)',
                        border: '1px solid rgba(102, 126, 234, 0.3)',
                        fontSize: '0.8rem'
                      }}
                    >
                      {job.industry || 'General'}
                    </Badge>
                    <Badge 
                      style={{
                        backgroundColor: 'rgba(102, 126, 234, 0.2)',
                        color: 'rgba(255,255,255,0.8)',
                        border: '1px solid rgba(102, 126, 234, 0.3)',
                        fontSize: '0.8rem'
                      }}
                    >
                      {job.jobType || 'Full-time'}
                    </Badge>
                    <Badge 
                       style={{
                          backgroundColor: 'rgba(118, 75, 162, 0.2)',
                          color: 'rgba(255,255,255,0.8)',
                          border: '1px solid rgba(118, 75, 162, 0.3)',
                          fontSize: '0.8rem'
                        }}
                      >
                          via {(() => {
                        // Extract source from id field
                        if (job.id) {
                          // Extract the source from id (e.g., "adzuna_5386748658" -> "Adzuna")
                          const match = job.id.match(/^([a-zA-Z]+)[_-]/);
                          if (match) {
                            // Capitalize first letter for display
                            const source = match[1];
                            return source.charAt(0).toUpperCase() + source.slice(1).toLowerCase();
                          }
                        }
                        return 'Unknown';
                      })()}
                    </Badge>
                  </Flex>
                </Flex>
              </Card>

              <Card
                padding="1.5rem"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(102, 126, 234, 0.3)'
                }}
              >
                <Heading level={4} style={{ color: 'white', marginBottom: '1rem' }}>
                  Job Description
                </Heading>
                
                {loadingFullDescription ? (
                  <Flex direction="column" alignItems="center" gap="1rem" padding="2rem">
                    <Loader size="medium" />
                    <Text fontSize="0.9rem" color="rgba(255,255,255,0.8)">
                      Loading full job description...
                    </Text>
                  </Flex>
                ) : fullDescriptionError ? (
                  <Alert
                    variation="error"
                    style={{
                      backgroundColor: 'rgba(231, 76, 60, 0.2)',
                      border: '2px solid rgba(231, 76, 60, 0.5)',
                      color: 'white',
                      borderRadius: '8px',
                      marginBottom: '1rem'
                    }}
                  >
                    {fullDescriptionError}
                  </Alert>
                ) : (
                  <>
                    <div 
                      className="job-description"
                      style={{ 
                        color: 'rgba(255,255,255,0.8)',
                        lineHeight: '1.8',
                        wordBreak: 'break-word',
                        whiteSpace: (job.source === 'adzuna-api' || (job.id && job.id.toLowerCase().startsWith('adzuna'))) ? 'pre-wrap' : 'normal'
                      }}
                      dangerouslySetInnerHTML={{ 
                        __html: DOMPurify.sanitize(formatJobDescription(job))
                      }}
                    />
                    
                    {job.descriptionEnhanced && (
                      <Badge 
                        style={{
                          marginTop: '1rem',
                          backgroundColor: 'rgba(46, 204, 113, 0.2)',
                          color: '#2ECC71',
                          border: '1px solid rgba(46, 204, 113, 0.3)',
                          fontSize: '0.75rem'
                        }}
                      >
                        Full Description Loaded
                      </Badge>
                    )}
                  </>
                )}
                
                {job.requirements && job.requirements.length > 0 && (
                  <>
                    <Heading level={4} style={{ color: 'white', marginTop: '1.5rem', marginBottom: '1rem' }}>
                      Requirements
                    </Heading>
                    <ul style={{ 
                      color: 'rgba(255,255,255,0.8)', 
                      paddingLeft: '1.5rem',
                      lineHeight: '1.6'
                    }}>
                      {job.requirements.map((req, index) => (
                        <li key={index} style={{ marginBottom: '0.5rem', wordBreak: 'break-word' }}>{req}</li>
                      ))}
                    </ul>
                  </>
                )}
              </Card>
            </View>

            {shouldShowCompatibility && (
              <View flex="1" style={{ minWidth: '350px' }}>
                <Card
                  padding="1.5rem"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    borderRadius: '8px',
                    border: '1px solid rgba(102, 126, 234, 0.3)',
                    height: '100%'
                  }}
                >
                  <Heading level={4} style={{ color: 'white', marginBottom: '1.5rem', textAlign: 'center' }}>
                    {readOnly ? 'Your CV Compatibility' : 'CV Compatibility Analysis'}
                  </Heading>
                  
                  {isCheckingCompatibility && !readOnly && (
                    <Flex direction="column" alignItems="center" gap="1rem" padding="2rem">
                      <Loader size="large" />
                      <Text fontSize="1rem" color="rgba(255,255,255,0.8)">
                        Analysing your CV against this position...
                      </Text>
                    </Flex>
                  )}
                  
                  {compatibilityError && !isCheckingCompatibility && !readOnly && (
                    <Alert
                      variation="error"
                      style={{
                        backgroundColor: 'rgba(231, 76, 60, 0.2)',
                        border: '2px solid rgba(231, 76, 60, 0.5)',
                        color: 'white',
                        borderRadius: '8px',
                        marginBottom: '1rem'
                      }}
                    >
                      {compatibilityError}
                    </Alert>
                  )}
                  
                  {compatibilityResult && (!isCheckingCompatibility || readOnly) && (
                    <Flex direction="column" gap="1.5rem">
                      {/* Compatibility Score */}
                      <Card 
                        borderRadius="12px"
                        padding="1.5rem"
                        backgroundColor="rgba(255,255,255,0.05)"
                        style={{ 
                          boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
                          backdropFilter: 'blur(20px)',
                          border: '1px solid rgba(255,255,255,0.1)'
                        }}
                      >
                        <Flex direction="column" gap="1rem">
                          <Flex justifyContent="space-between" alignItems="center">
                            <Text fontWeight="bold" fontSize="1.1rem" color="white">Compatibility Score</Text>
                            <View 
                              backgroundColor={getCompatibilityColor(compatibilityResult.score)}
                              padding="0.25rem 0.75rem"
                              borderRadius="20px"
                            >
                              <Text color="white" fontSize="0.8rem" fontWeight="bold">
                                {getCompatibilityLabel(compatibilityResult.score)}
                              </Text>
                            </View>
                          </Flex>
                          
                          <Flex direction="column" gap="0.5rem">
                            <View 
                              backgroundColor="rgba(255,255,255,0.1)" 
                              height="12px" 
                              borderRadius="6px"
                              overflow="hidden"
                            >
                              <View 
                                backgroundColor={getCompatibilityColor(compatibilityResult.score)}
                                height="12px" 
                                width={`${compatibilityResult.score}%`}
                                style={{
                                  borderRadius: '6px',
                                  transition: 'width 0.5s ease-in-out'
                                }}
                              />
                            </View>
                            <Flex justifyContent="center" alignItems="baseline" gap="0.25rem">
                              <Text fontWeight="bold" fontSize="1.5rem" color="white">{compatibilityResult.score}</Text>
                              <Text fontSize="1rem" color="rgba(255,255,255,0.7)">/100</Text>
                            </Flex>
                          </Flex>
                        </Flex>
                      </Card>
                      
                      {/* Analysis Summary */}
                      <Card 
                        borderRadius="10px" 
                        padding="1.5rem" 
                        backgroundColor="rgba(52, 152, 219, 0.15)"
                        border="1px solid rgba(52, 152, 219, 0.3)"
                      >
                        <Flex direction="column" gap="0.5rem">
                          <Text color="#67b8ff" fontWeight="bold" textAlign="center">Analysis Summary</Text>
                          <Text 
                            color="rgba(255,255,255,0.85)" 
                            fontSize="0.95rem" 
                            style={{ 
                              whiteSpace: 'pre-line',
                              textAlign: 'center',
                              lineHeight: '1.6'
                            }}
                          >
                            {compatibilityResult.explanation}
                          </Text>
                        </Flex>
                      </Card>
                      
                      {/* Key Matches */}
                      {compatibilityResult.key_matches && compatibilityResult.key_matches.length > 0 && (
                        <Card 
                          borderRadius="10px" 
                          padding="1.5rem" 
                          backgroundColor="rgba(46, 204, 113, 0.15)"
                          border="1px solid rgba(46, 204, 113, 0.3)"
                        >
                          <Flex direction="column" gap="0.75rem">
                            <Text color="#2ECC71" fontWeight="bold" textAlign="center">Key Matches</Text>
                            <Flex direction="column" gap="0.5rem">
                              {compatibilityResult.key_matches.map((match, index) => (
                                <Flex key={index} alignItems="flex-start" gap="0.5rem">
                                  <Text color="#2ECC71" fontSize="1rem">✓</Text>
                                  <Text 
                                    color="rgba(255,255,255,0.85)" 
                                    fontSize="0.9rem" 
                                    style={{ lineHeight: '1.5' }}
                                  >
                                    {match}
                                  </Text>
                                </Flex>
                              ))}
                            </Flex>
                          </Flex>
                        </Card>
                      )}
                      
                      {/* Missing Skills */}
                      {compatibilityResult.missing_skills && compatibilityResult.missing_skills.length > 0 && (
                        <Card 
                          borderRadius="10px" 
                          padding="1.5rem" 
                          backgroundColor="rgba(231, 76, 60, 0.15)"
                          border="1px solid rgba(231, 76, 60, 0.3)"
                        >
                          <Flex direction="column" gap="0.75rem">
                            <Text color="#E74C3C" fontWeight="bold" textAlign="center">Areas for Development</Text>
                            <Flex direction="column" gap="0.5rem">
                              {compatibilityResult.missing_skills.map((skill, index) => (
                                <Flex key={index} alignItems="flex-start" gap="0.5rem">
                                  <Text color="#E74C3C" fontSize="1rem">•</Text>
                                  <Text 
                                    color="rgba(255,255,255,0.85)" 
                                    fontSize="0.9rem" 
                                    style={{ lineHeight: '1.5' }}
                                  >
                                    {skill}
                                  </Text>
                                </Flex>
                              ))}
                            </Flex>
                          </Flex>
                        </Card>
                      )}
                      
                      {/* Recommendations */}
                      {compatibilityResult.recommendations && compatibilityResult.recommendations.length > 0 && (
                        <Card 
                          borderRadius="10px" 
                          padding="1.5rem" 
                          backgroundColor="rgba(155, 89, 182, 0.15)"
                          border="1px solid rgba(155, 89, 182, 0.3)"
                        >
                          <Flex direction="column" gap="0.75rem">
                            <Text color="#9B59B6" fontWeight="bold" textAlign="center">Recommendations</Text>
                            <Flex direction="column" gap="0.5rem">
                              {compatibilityResult.recommendations.map((recommendation, index) => (
                                <Flex key={index} alignItems="flex-start" gap="0.5rem">
                                  <Text color="#9B59B6" fontSize="1rem">→</Text>
                                  <Text 
                                    color="rgba(255,255,255,0.85)" 
                                    fontSize="0.9rem" 
                                    style={{ lineHeight: '1.5' }}
                                  >
                                    {recommendation}
                                  </Text>
                                </Flex>
                              ))}
                            </Flex>
                          </Flex>
                        </Card>
                      )}
                    </Flex>
                  )}
                </Card>
              </View>
            )}
          </Flex>
        </View>
      </div>
    </div>
  );
};

export default JobDetailsModal;